import { Component } from '@theme/component';

/**
 * Appointment scheduler component that embeds an iframe and dynamically adjusts
 * its height and viewport based on postMessage communication from BridalLive.
 *
 * @typedef {object} AppointmentSchedulerRefs
 * @property {HTMLIFrameElement} iframe - The embedded iframe element
 *
 * @extends {Component<AppointmentSchedulerRefs>}
 */
export class AppointmentSchedulerComponent extends Component {
  requiredRefs = ['iframe'];

  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    super.connectedCallback();

    const { iframe } = this.refs;
    if (!iframe) return;

    this.#abortController = new AbortController();
    window.addEventListener('message', this.handleMessage, {
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#abortController = null;
  }

  /**
   * Handle incoming postMessage events from BridalLive
   * @param {MessageEvent} event
   */
  handleMessage = (event) => {
    if (!event.data) return;

    const { iframe } = this.refs;
    if (!iframe) return;

    // Verify message source matches this iframe if contentWindow is available
    if (iframe.contentWindow && event.source && event.source !== iframe.contentWindow) {
      return;
    }

    let data = event.data;
    if (typeof data === 'string') {
      try {
        data = JSON.parse(data);
      } catch (_) {
        return;
      }
    }

    if (!data || typeof data !== 'object') return;

    // Handle dynamic height change from scheduler
    if (data.type === 'bridallive.height_change') {
      const height = data.value ?? data.height ?? data.heights?.content ?? data.heights?.root;
      if (typeof height === 'number' && height > 200) {
        iframe.style.height = `${height + 30}px`;
      }
    } else if (data.type === 'bridallive.scroll-to-top') {
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      iframe.scrollIntoView({
        behavior: prefersReducedMotion ? 'auto' : 'smooth',
        block: 'start',
      });
    }
  };
}

if (!customElements.get('appointment-scheduler-component')) {
  customElements.define('appointment-scheduler-component', AppointmentSchedulerComponent);
}
